import clsx from "clsx";
import { Skeleton } from "antd";
import { useState, useEffect, useMemo, useRef } from "react";
import { OrderInfo } from "lib/types";
import { getLicenseOrders } from "pages/dashboard/license/service";
import { getCurrencySymbol } from "lib/utils";
import { useParams, useNavigate } from "react-router-dom";
import { showToast } from "ui/components/common";

const PageSize = 20;
export default function OrderList() {
  const navigate = useNavigate();
  const params = useParams();

  const [ordersData, setOdersData] = useState<OrderInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const pageOffsetRef = useRef(0);
  const hasNextRef = useRef(false);

  const currentOrderId = useMemo(() => {
    return Number(params.id);
  }, [params]);

  useEffect(() => {
    setOdersData([]);
    queryOrders(0);
    return () => {
      setOdersData([]);
    };
  }, []);

  const queryOrders = async (offset, append: boolean = false) => {
    try {
      setIsLoading(true);
      const response = await getLicenseOrders(PageSize, offset);
      if (response.status !== 200) {
        showToast(1, "Failed to fetch orders");
        return;
      }
      const data = response.data;
      hasNextRef.current = data.paging?.total / PageSize > offset + 1;
      if (append) {
        console.log("queryOrders", ordersData);
        setOdersData([...ordersData, ...data.data]);
      } else {
        setOdersData(data?.data);
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!ordersData || !hasNextRef.current) {
      return;
    }
    const observer = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            pageOffsetRef.current++;
            queryOrders(pageOffsetRef.current, true);
            // stop observer
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );
    const targetElement = document.getElementById("needLoadMore");
    if (targetElement) {
      observer.observe(targetElement);
    }
  }, [ordersData]);

  if (isLoading && ordersData?.length === 0)
    return (
      <div className="text-sm text-center text-neutral-500 pb-3 mt-4">
        <Skeleton title={false} />
        <Skeleton title={false} className="mt-6" />
        <Skeleton title={false} className="mt-6" />
      </div>
    );

  if (!isLoading && ordersData?.length === 0)
    return (
      <div className="text-sm text-center text-neutral-500 pb-3">No Order</div>
    );

  const paymentStatus = (e: OrderInfo) => {
    switch (e.status) {
      case "confirmed":
      case "paid":
        if (e.isRefunded) {
          return <p className="text-[#FF9800] font-semibold">Refund</p>;
        }
        return <p className="text-[#179954] font-semibold">Paid</p>;

      default:
        return <p className="text-[#717171] font-semibold">Unpaid</p>;
    }
  };

  function handleClicked(id) {
    navigate(`/dashboard/license/history/${id}`);
  }

  return (
    <div className="w-full pt-4" id="scrollableDiv">
      {ordersData?.map((e: OrderInfo) => (
        <button
          key={e.id}
          onClick={() => handleClicked(e.id)}
          className={clsx(
            "flex justify-between items-center flex-row w-full gap-x-2 p-6 h-[90px] hover:bg-[#DCE7FD] rounded-3xl mb-4 text-neutral-800",
            currentOrderId === e.id ? "bg-[#DCE7FD]" : "bg-[#F5F5F5]",
          )}
        >
          <div className="text-left text-sm">
            <p>{e.invoiceDate || "-"}</p>
            <p>Invoice #:{e.no || "-"}</p>
          </div>
          <div>
            {paymentStatus(e)}
            <p className="font-semibold min-w-14">
              {getCurrencySymbol(e?.currency) +
                " " +
                (e?.amount / 100)?.toLocaleString()}
            </p>
          </div>
        </button>
      ))}
      <div
        className="text-sm text-center text-neutral-500 h-1"
        id="needLoadMore"
      />
      {isLoading && (
        <div className="text-sm text-center text-neutral-500 pb-3 pt-1">
          Loading...
        </div>
      )}
      {!isLoading && !hasNextRef.current && ordersData?.length > PageSize && (
        <p className="text-sm text-center text-neutral-500 pb-3 pt-1">
          No More Order
        </p>
      )}
    </div>
  );
}
